import {
  ViewpointBuilderContext,
  ViewpointBuilderViewState,
} from 'viewpointBuilder/types';
import {
  GetViewpointBuilderStreamArgs,
  getViewpointBuilderStream,
} from './getViewpointBuilderStream';
import {
  getUpdatePerspectivesOptionsRoutine,
  getNavigationTabDetailsOnCollapsedPaths,
  getNavigationTabDetailsOnCollapsedPathsUnselect,
  getNavigationTabDetailsOnFormattingRoutine,
  getNavigationTabDetailsOnGraphRoutine,
  getNavigationTabDetailsOnGroupingRoutine,
  getNavigationTabDetailsOnMetaInfoRoutine,
  getNavigationTabDetailsOnRequiredComponents,
  getNavigationTabDetailsOnSelectedComponentsRoutine,
  getTrackSelectTabRoutine,
  getUpdateAdvancedSearchQueryRoutine,
  getUpdateInstanceCountsRoutine,
  getUpdateTraversalStartContextRoutine,
} from './viewpointBuilderRoutines';
import {
  action$,
  collectRoutines,
  Routine,
  subscribeRoutine,
} from '@ardoq/rxbeach';
import { resetSharedSearchState } from '../resetViewpointBuilder';
import {
  getFetchOptionInstanceCountsRoutine,
  getRequestFetchInstanceCountsRoutine,
} from '../instanceCountsRoutines';
import { getHandleTraversalSearchRoutine } from '../selectContextTab/componentSearchRoutines';
import { getMigrateFormattingRulesRoutine } from '../formattingTabs/routines';
import {
  createViewpointBuilderStreams,
  ViewpointBuilderStreams,
} from './viewpointBuilder$';

type SubscribeToStreamsAndRoutinesArgs = Omit<
  GetViewpointBuilderStreamArgs,
  'streams'
> & {
  namespace: string;
  setState: (state: ViewpointBuilderViewState) => void;
  onFirstSubscriptionCallback?: () => void;
};

export const subscribeToViewpointBuilderStreamAndRoutines = ({
  namespace,
  activeTab,
  context,
  initialConfiguration,
  folderId,
  viewpointBuilderCommands,
  setState,
  getAdditionalRoutines = () => [],
  onFirstSubscriptionCallback,
}: SubscribeToStreamsAndRoutinesArgs) => {
  const streams = createViewpointBuilderStreams(namespace);

  const viewpointBuilderStream = getViewpointBuilderStream({
    streams,
    activeTab,
    context,
    initialConfiguration,
    folderId,
    viewpointBuilderCommands,
    onFirstSubscriptionCallback,
  });

  const subscriptions = [
    viewpointBuilderStream.subscribe(setState),
    subscribeRoutine(
      action$,
      getViewpointBuilderRoutines(
        streams,
        context,
        getAdditionalRoutines(viewpointBuilderStream)
      )
    ),
  ];

  return () => {
    subscriptions.forEach(subscription => subscription.unsubscribe());
    resetSharedSearchState();
  };
};

export const getViewpointBuilderRoutines = (
  streams: ViewpointBuilderStreams,
  context: ViewpointBuilderContext,
  additionalRoutines: Routine<unknown>[] = []
) => {
  const { namespace } = streams;
  return collectRoutines(
    /**
     * Temporary state routines, updating stream input from another stream
     * output (e.g. route search component stream output to traversal edit
     * input) or trigger async updates (e.g. fetch instance counts).
     */
    getUpdatePerspectivesOptionsRoutine(
      context,
      streams.editTraversals$,
      namespace
    ),
    getUpdateInstanceCountsRoutine(streams.editTraversals$, namespace),
    getUpdateTraversalStartContextRoutine(streams.componentSearch$, namespace),
    getUpdateAdvancedSearchQueryRoutine(streams.componentSearch$, namespace),
    getNavigationTabDetailsOnSelectedComponentsRoutine(
      streams.componentSearch$,
      namespace
    ),
    getNavigationTabDetailsOnGraphRoutine(streams.editTraversals$, namespace),
    getNavigationTabDetailsOnFormattingRoutine(
      streams.viewpointFormatting$,
      streams.viewpointBuilderNavigation$,
      namespace
    ),
    getNavigationTabDetailsOnGroupingRoutine(
      streams.viewpointGroupingRules$,
      namespace
    ),
    getNavigationTabDetailsOnMetaInfoRoutine(streams.metaInfo$, namespace),
    getNavigationTabDetailsOnCollapsedPaths(streams.editTraversals$, namespace),
    getNavigationTabDetailsOnRequiredComponents(
      streams.editTraversals$,
      namespace
    ),
    getNavigationTabDetailsOnCollapsedPathsUnselect(
      streams.viewpointBuilderNavigation$,
      namespace
    ),
    getTrackSelectTabRoutine(streams.metaInfo$, namespace),
    getRequestFetchInstanceCountsRoutine(namespace),
    getFetchOptionInstanceCountsRoutine(namespace),
    getHandleTraversalSearchRoutine(streams.componentSearch$, namespace),
    getMigrateFormattingRulesRoutine(
      streams.viewpointFormattingState$,
      namespace
    ),
    ...additionalRoutines
  );
};
